import { db } from "../api/firebase";
import { ref, onValue, get } from "firebase/database";

import { canonicalEcardPreset } from "../utils/ecardPresetNormalize";
import {
  computeExpiresAtMs,
  formatExpiryDate,
  isExpired,
  parseListedAtMs,
  parseValidityMonths,
} from "../utils/giftExpiry";

/**
 * Maps RTDB `gifts/{id}` to the fields we use in the app (supports common key spellings).
 * Returns null if the catalog row is past `listedAt` + `validityMonths` (not shown; server job deletes it).
 */
export function mapGiftToSummary(id, raw) {
  if (!raw || typeof raw !== "object") return null;
  const name = raw.name ?? raw.Name ?? raw.Tittle ?? raw.title ?? "";
  const description = raw.description ?? raw.Description ?? "";
  const price = Number(raw.Price ?? raw.price ?? 0);
  const discount = Number(raw.Discount ?? raw.discount ?? 0);
  const legacyValidityStr = String(raw.Validity ?? raw.validity ?? raw.validUntil ?? "").trim();

  const validityMonths = parseValidityMonths(raw);
  const listedAt = parseListedAtMs(raw);
  const expiresAtMs = computeExpiresAtMs(listedAt, validityMonths);

  if (isExpired(expiresAtMs)) {
    return null;
  }

  const validityLabel =
    validityMonths > 0 && expiresAtMs
      ? `${validityMonths} mo · ends ${formatExpiryDate(expiresAtMs)}`
      : validityMonths > 0
        ? `${validityMonths} mo (set listedAt for end date)`
        : legacyValidityStr || "";

  const rawPreset =
    raw.ecardPreset ?? raw.EcardPreset ?? raw.preset ?? raw.Type ?? "";
  const ecardPreset = canonicalEcardPreset(rawPreset);

  const coverUrl = [raw.imageurl, raw.imageUrl, raw.Image1, raw.coverurl, raw.CoverUrl]
    .map((u) => (typeof u === "string" ? u.trim() : ""))
    .find(Boolean);
  const image2 = raw.image2 ?? raw.image2Url ?? raw.Image2 ?? "";
  const image3 = raw.image3 ?? raw.image3Url ?? raw.Image3 ?? "";

  return {
    id,
    name: String(name).trim(),
    description: String(description).trim(),
    price,
    discount,
    validity: legacyValidityStr,
    validityMonths,
    listedAt,
    expiresAtMs,
    validityLabel,
    ecardPreset,
    coverUrl: coverUrl || "",
    image2: typeof image2 === "string" ? image2.trim() : "",
    image3: typeof image3 === "string" ? image3.trim() : "",
  };
}

export function subscribeGiftCardSummaries(callback) {
  if (!db) {
    callback([]);
    return () => {};
  }
  const giftsRef = ref(db, "gifts");
  return onValue(
    giftsRef,
    (snapshot) => {
      const data = snapshot.val() || {};
      const rows = Object.keys(data)
        .map((key) => mapGiftToSummary(key, data[key]))
        .filter(Boolean);
      callback(rows);
    },
    (err) => {
      console.error("[subscribeGiftCardSummaries]", err);
      callback([]);
    }
  );
}

export async function getGiftCardSummaryById(id) {
  if (!db || !id) return null;
  const snap = await get(ref(db, `gifts/${id}`));
  if (!snap.exists()) return null;
  return mapGiftToSummary(id, snap.val());
}
